/* eslint-disable camelcase */
import {
  Button,
  HStack,
  Icon as ChakraIcon,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  FormLabel,
  VStack,
  Flex,
  Box,
  Skeleton,
} from '@chakra-ui/react';
import {
  ColumnDef,
  Table as TableType,
  useReactTable,
  getPaginationRowModel,
  flexRender,
  getCoreRowModel,
  Header,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  calculateMarginAvailability,
  parse,
  getMarginAvailabilityStatus,
  getZeroOrRounded,
} from '@finley-technologies/valley-bank-utils';
import type {
  ValleyBankLoanAccountWithCollateralStats,
  ValleyBankMarginAvailabilityStatus,
} from '@finley-technologies/valley-bank-utils';

import {
  PaginationInfoGQLType,
  useGetAllValleyBankLoanAccountsLazyQueryGQLType,
  ValleyBankLoanAccountQueryParamsGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from '~/packages/common-frontend/colors';
import { Alert } from '~/packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Select } from '~/packages/common-frontend/components/Select';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  TableColumnConfigurationKey,
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
  getColumnNames,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import { frozenHeaderRowStyling } from '~/packages/common-frontend/styles';
import {
  scrollToTopOfElement,
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { ArrangementGroupSummaryLink } from '~/valleyBank/domains/ArrangementGroups/hooks/ArrangementGroupsContext';
import { SearchPopover } from '~/valleyBank/domains/components/SearchPopover';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { LoanAccountSummaryLink } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

type OrderByConfig = {
  columnName: string;
  desc: boolean;
};

const TABLE_KEY: TableColumnConfigurationKey = 'valleyBankSbl.loanAccounts';

const DEFAULT_TABLE_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: [
    { columnKey: 'loanAccountNumber', isVisible: true, isFixed: false },
    { columnKey: 'borrower', isVisible: true, isFixed: false },
    { columnKey: 'outstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'assignedMarketValue', isVisible: true, isFixed: false },
    { columnKey: 'effectiveAdvanceRate', isVisible: true, isFixed: false },
    {
      columnKey: 'assignedPreAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocationRate',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'assignedPostAllocationLendableValue',
      isVisible: false,
      isFixed: false,
    },
    { columnKey: 'marginAvailability', isVisible: true, isFixed: false },
    { columnKey: 'marginAvailabilityRate', isVisible: false, isFixed: false },
    { columnKey: 'arrangementGroupId', isVisible: true, isFixed: false },
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'numberOfLoanParties', isVisible: true, isFixed: false },
  ],
});

const PAGINATION_LIMIT = 100;

const SEARCH_BY_OPTIONS = [
  { value: 'loanAccountNumber' as const, label: 'Loan Account Number' },
  { value: 'customerName' as const, label: 'Customer Name' },
];

type SearchByOptionType = typeof SEARCH_BY_OPTIONS[number]['value'];

const getStatusColor = (status: ValleyBankMarginAvailabilityStatus): string => {
  switch (status) {
    case 'excessMargin':
      return colors.success;
    case 'nearMargin':
      return colors.warning;
    case 'underMargin':
      return colors.error;
    default:
      return finleyAssertUnreachable(status);
  }
};

const getStatusIcon = (status: ValleyBankMarginAvailabilityStatus): string => {
  switch (status) {
    case 'excessMargin':
      return 'circle-check';
    case 'nearMargin':
    case 'underMargin':
    default:
      return 'circle-exclamation';
  }
};

type StatusIconProps = { color: string; icon: string };
const StatusIcon = ({ color, icon }: StatusIconProps) => (
  <ChakraIcon color={color}>
    <Icon icon={icon} />
  </ChakraIcon>
);

type StatusBadgeProps = {
  loanOutstanding: number;
  collateralLendableValue: number;
  marginAvailabilityRate: number;
};
const StatusBadge = ({
  marginAvailabilityRate,
  loanOutstanding,
  collateralLendableValue,
}: StatusBadgeProps) => {
  const status = getMarginAvailabilityStatus({
    marginAvailabilityRate,
    loanOutstanding,
    collateralLendableValue,
  });
  const color = getStatusColor(status);
  const icon = getStatusIcon(status);
  return (
    <HStack spacing="16px" p="16px" justifyContent="flex-start">
      <StatusIcon icon={icon} color={color} />
      <Text color={colors.black[70]}>{`${commonUtils.toTitleCase(
        status,
      )} ${percentageToPercentageLabel(
        marginAvailabilityRate * 100,
        2,
      )}`}</Text>
    </HStack>
  );
};

type PaginationControlButtonsProps = Partial<{
  isPreviousDisabled: boolean;
  isNextDisabled: boolean;
  onClickPrevious: () => void;
  onClickNext: () => void;
}>;
const PaginationControlButtons = ({
  isPreviousDisabled,
  onClickPrevious,
  isNextDisabled,
  onClickNext,
}: PaginationControlButtonsProps) => {
  return (
    <HStack justify="space-between" mt="32px" w="100%">
      <Button
        variant="ghost"
        isDisabled={isPreviousDisabled || isPreviousDisabled === undefined}
        onClick={onClickPrevious}
      >
        Previous
      </Button>
      <Button
        variant="ghost"
        isDisabled={isNextDisabled || isNextDisabled === undefined}
        onClick={onClickNext}
      >
        Next
      </Button>
    </HStack>
  );
};

type PaginationControlProps = {
  paginationInfo?: PaginationInfoGQLType;
  fetchNextPage: (input: { limit: number; offset: number }) => void;
};
const PaginationControl = ({
  fetchNextPage,
  paginationInfo,
}: PaginationControlProps) => {
  if (!paginationInfo) {
    return <PaginationControlButtons />;
  }

  const { totalCount, offset: currentOffset, limit } = paginationInfo;

  const hasPrevious = currentOffset > 0;
  const hasNext = currentOffset + limit < totalCount;

  return (
    <PaginationControlButtons
      isNextDisabled={!hasNext}
      isPreviousDisabled={!hasPrevious}
      onClickNext={() =>
        fetchNextPage({ limit, offset: currentOffset + limit })
      }
      onClickPrevious={() =>
        fetchNextPage({ limit, offset: currentOffset - limit })
      }
    />
  );
};

export const loanAccountsColumnDefs: ColumnDef<
  ValleyBankLoanAccountWithCollateralStats,
  any
>[] = [
  {
    id: 'loanAccountNumber',
    accessorKey: 'loanAccountNumber',
    header: 'Loan Account',
    cell: function LoanAccountNumber({ row, getValue }) {
      return (
        <LoanAccountSummaryLink loanAccountId={row.original.loanAccountNumber}>
          {getValue()}
        </LoanAccountSummaryLink>
      );
    },
    enableSorting: false,
  },
  {
    id: 'arrangementGroupId',
    accessorKey: 'arrangementGroupId',
    header: 'Arrangement Group',
    cell: function ArrangmentGroup({ getValue }) {
      const arrangementGroup = getValue();
      return arrangementGroup ? (
        <ArrangementGroupSummaryLink arrangementGroup={arrangementGroup}>
          {arrangementGroup}
        </ArrangementGroupSummaryLink>
      ) : (
        '-'
      );
    },
    enableSorting: false,
  },
  {
    id: 'cifNum',
    header: 'Borrower CIF Number',
    accessorKey: 'cifNum',
    cell: function Customer({ row }) {
      const { cifNum } = row.original;
      return (
        <CustomerSummaryLink cifnum={cifNum}>
          {cifNum || '-'}
        </CustomerSummaryLink>
      );
    },
    enableSorting: false,
  },
  {
    id: 'borrower',
    accessorFn: (loanAccount) => {
      const { cifNum, loanParties } = loanAccount;
      const customer = loanParties.find(
        (loanParty) => loanParty.cif_num === cifNum,
      );

      return customer?.full_name;
    },
    header: 'Borrower',
    cell: function Customer({ row, getValue }) {
      const { cifNum } = row.original;
      return (
        <CustomerSummaryLink cifnum={cifNum}>
          {getValue() || '-'}
        </CustomerSummaryLink>
      );
    },
    enableSorting: false,
  },
  {
    id: 'numberOfLoanParties',
    header: 'Number of Loan Parties',
    accessorFn: (loanAccount) => loanAccount.loanParties.length,
    enableSorting: false,
  },
  // these stats are sortable and need an id that matches the `valley_bank_loan_account_stats` table to work
  {
    id: 'outstandingBalance',
    accessorFn: ({ principalBalance }) => principalBalance,
    cell: ({ getValue }) => formatCurrencyAmount(getValue(), Bank.Currency.USD),
    header: 'Outstanding Balance',
    enableSorting: true,
  },
  {
    id: 'assignedMarketValue',
    header: 'Collateral Market Value',
    accessorFn: ({ totalMarketValue }) => totalMarketValue,
    cell: ({ getValue }) => formatCurrencyAmount(getValue(), Bank.Currency.USD),
    enableSorting: true,
  },
  {
    id: 'effectiveAdvanceRate',
    header: 'Collateral Advance Rate',
    accessorFn: ({ effectiveAdvanceRate }) => effectiveAdvanceRate,
    cell: ({ getValue }) => percentageToPercentageLabel(getValue() * 100, 2),
    enableSorting: true,
  },
  {
    id: 'assignedPreAllocationLendableValue',
    header: 'Collateral Pre Allocation Lendable Value',
    accessorFn: ({ totalLendableValue }) => totalLendableValue,
    cell: ({ getValue }) => formatCurrencyAmount(getValue(), Bank.Currency.USD),
    enableSorting: true,
  },
  {
    id: 'effectiveAllocationRate',
    header: 'Collateral Effective Allocation',
    accessorFn: ({ effectiveAllocationRate }) => effectiveAllocationRate,
    cell: ({ getValue }) => percentageToPercentageLabel(getValue() * 100, 2),
    enableSorting: true,
  },
  {
    id: 'assignedPostAllocationLendableValue',
    header: 'Collateral Post Allocation Lendable Value',
    accessorFn: ({ totalPostAllocationLendableValue }) =>
      totalPostAllocationLendableValue,
    cell: ({ getValue }) => formatCurrencyAmount(getValue(), Bank.Currency.USD),
    enableSorting: true,
  },
  {
    id: 'marginAvailability',
    header: 'Margin Availability',
    accessorFn: ({ totalPostAllocationLendableValue, principalBalance }) =>
      calculateMarginAvailability({
        collateralLendableValue: totalPostAllocationLendableValue,
        loanOutstanding: principalBalance,
      }),
    cell: function MarginAvailability({ getValue }) {
      const marginAvailability = getValue();
      const { roundedValue } = getZeroOrRounded(marginAvailability);

      return formatCurrencyAmount(roundedValue, Bank.Currency.USD);
    },
    enableSorting: true,
  },
  {
    id: 'marginAvailabilityRate',
    header: 'Margin Availability Rate',
    accessorKey: 'marginAvailabilityRate',
    cell: function MarginAvailabilityRate({ row }) {
      const {
        marginAvailabilityRate,
        principalBalance,
        totalLendableValue,
      } = row.original;

      const { isZero } = getZeroOrRounded(marginAvailabilityRate * 100);

      return (
        <StatusBadge
          marginAvailabilityRate={isZero ? 0 : marginAvailabilityRate}
          loanOutstanding={principalBalance}
          collateralLendableValue={totalLendableValue}
        />
      );
    },
    enableSorting: true,
  },
];

export const LOAN_ACCOUNT_NAMES = getColumnNames(loanAccountsColumnDefs);

type LoanAccountsTableContainerProps = {
  table: TableType<ValleyBankLoanAccountWithCollateralStats>;
  fetchInitialLoanAccountsOrderByPage?: (
    orderByParam: OrderByConfig | null,
  ) => void;
  isLoading: boolean;
  isError?: boolean;
};

export const LoanAccountsTableContainer = ({
  table,
  fetchInitialLoanAccountsOrderByPage,
  isLoading,
  isError,
}: LoanAccountsTableContainerProps) => {
  const onHandleSortClick = useCallback(
    ({
      header,
      fetchInitialLoanAccountsOrderByPage: _fetchInitialLoanAccountsOrderByPage,
    }: {
      header: Header<ValleyBankLoanAccountWithCollateralStats, any>;
      fetchInitialLoanAccountsOrderByPage?: (
        orderByParam: OrderByConfig | null,
      ) => void;
    }) => {
      return () => {
        const nextSortingOrder = header.column.getNextSortingOrder();
        const columnName = header.column.columnDef.id;
        header.column.toggleSorting();
        if (_fetchInitialLoanAccountsOrderByPage && columnName) {
          if (typeof nextSortingOrder !== 'string') {
            _fetchInitialLoanAccountsOrderByPage(null);
          } else {
            _fetchInitialLoanAccountsOrderByPage({
              columnName,
              desc: nextSortingOrder === 'desc',
            });
          }
        }
      };
    },
    [],
  );

  return (
    <TableContainer
      mt="32px"
      borderWidth="medium"
      borderColor={colors.gray[200]}
      // maxH required for table top sticky header to work
      // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
      // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
      maxHeight="50vh"
      overflowX="auto"
      overflowY="auto"
      position="relative"
      id="receivables-dashboard-main-table"
      w="100%"
    >
      <Table id="valley-bank-loanAccounts-table">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                    textAlign={header.column.columnDef.meta?.style?.textAlign}
                    minWidth={header.column.columnDef.meta?.style?.minWidth}
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={onHandleSortClick({
                          header,
                          fetchInitialLoanAccountsOrderByPage,
                        })}
                        mr="8px"
                        color={colors.aurora[400]}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        {isLoading && !isError && (
          <Tbody>
            {Array.from(Array(10).keys()).map((i) => (
              <Tr key={i}>
                {table.getVisibleFlatColumns().map((column) => (
                  <Td
                    key={`${column.id}+${i}`}
                    padding="8px 16px"
                    height="56px"
                  >
                    <Skeleton
                      h="100%"
                      w={
                        column.columnDef.header === 'Borrower'
                          ? '400px'
                          : undefined
                      }
                    />
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        )}
        {!isLoading && !isError && (
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    textAlign={cell.column.columnDef.meta?.style?.textAlign}
                    padding={cell.column.columnDef.meta?.style?.padding}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        )}
      </Table>
    </TableContainer>
  );
};

type LoanAccountSearchProps = {
  searchCustomerName: string;
  searchLoanAccountNumber: string;
  setSearchCustomerName: (value: string) => void;
  setSearchLoanAccountNumber: (value: string) => void;
  fetchInitialLoanAccountsSearchPage: (input: {
    loanAccountNumber?: string;
    customerName?: string;
  }) => void;
};
const LoanAccountSearch = ({
  searchLoanAccountNumber,
  searchCustomerName,
  setSearchLoanAccountNumber,
  setSearchCustomerName,
  fetchInitialLoanAccountsSearchPage,
}: LoanAccountSearchProps) => {
  const [searchBy, setSearchBy] = useState<SearchByOptionType>(
    SEARCH_BY_OPTIONS[0].value, // default to `customerName`
  );

  const handleClear = useCallback(() => {
    if (searchBy === 'loanAccountNumber') {
      setSearchLoanAccountNumber('');
    } else if (searchBy === 'customerName') {
      setSearchCustomerName('');
    }
  }, [searchBy, setSearchLoanAccountNumber, setSearchCustomerName]);

  const handleSearch = useCallback(() => {
    if (searchBy === 'loanAccountNumber') {
      fetchInitialLoanAccountsSearchPage({
        loanAccountNumber: searchLoanAccountNumber,
      });
    } else if (searchBy === 'customerName') {
      fetchInitialLoanAccountsSearchPage({ customerName: searchCustomerName });
    }
  }, [
    fetchInitialLoanAccountsSearchPage,
    searchBy,
    searchLoanAccountNumber,
    searchCustomerName,
  ]);

  return (
    <SearchPopover
      dropdownTitle="Search Loan Accounts"
      onClear={handleClear}
      onSearch={handleSearch}
      bodyChildren={
        <VStack w="100%" spacing="24px">
          <HStack w="100%" spacing="16px">
            <FormLabel m="auto">Search By</FormLabel>
            <Select
              options={SEARCH_BY_OPTIONS}
              value={searchBy}
              onChange={(nextValue) => {
                if (nextValue) {
                  if (nextValue === 'customerName') {
                    setSearchCustomerName('');
                  } else if (nextValue === 'loanAccountNumber') {
                    setSearchLoanAccountNumber('');
                  }
                  setSearchBy(nextValue);
                }
              }}
            />
          </HStack>
          {searchBy === 'loanAccountNumber' && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon icon="magnifying-glass" />
              </InputLeftElement>
              <Input
                value={searchLoanAccountNumber}
                onChange={(e) => setSearchLoanAccountNumber(e.target.value)}
                placeholder="Loan Account Number"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </InputGroup>
          )}
          {searchBy === 'customerName' && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon icon="magnifying-glass" />
              </InputLeftElement>
              <Input
                value={searchCustomerName}
                onChange={(e) => setSearchCustomerName(e.target.value)}
                placeholder="Customer Name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </InputGroup>
          )}
        </VStack>
      }
    />
  );
};

export const LoanAccountsTable = () => {
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG,
  });

  const [loanAccounts, setLoanAccounts] = useState<
    ValleyBankLoanAccountWithCollateralStats[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [
    searchLoanAccountNumber,
    setSearchLoanAccountNumber,
  ] = useState<string>('');
  const [searchCustomerName, setSearchCustomerName] = useState<string>('');
  const [orderByParam, setOrderByParam] = useState<OrderByConfig | null>(null);
  const [
    queryParams,
    setQueryParams,
  ] = useState<ValleyBankLoanAccountQueryParamsGQLType | null>(null);

  const [
    getLoanAccounts,
    { data, error: isError },
  ] = useGetAllValleyBankLoanAccountsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const newLoanAccounts = parse.parseValleyBankLoanAccountWithCollateralStatsArray(
        res.allValleyBankLoanAccounts.loanAccounts,
      );
      if (newLoanAccounts[0]) {
        setAsOfDate(newLoanAccounts[0].asOfDate);
      }
      setLoanAccounts(newLoanAccounts);
      setIsLoading(false);
    },
  });

  const table = useReactTable({
    data: loanAccounts,
    columns: loanAccountsColumnDefs,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGINATION_LIMIT,
      },
    },
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // sorting is done on the server side
    // DO NOT ACTIVATE GET SORTED ROW MODEL FOR TANSTACK
  });

  const fetchLoanAccountsPage = useCallback(
    ({ limit, offset }: { limit: number; offset: number }) => {
      getLoanAccounts({
        variables: {
          reportingDate,
          isPreview,
          paginationOptions: { limit, offset },
          queryParams,
          orderByParam,
        },
      });
      scrollToTopOfElement('valley-bank-loanAccounts-table');
    },
    [getLoanAccounts, isPreview, queryParams, orderByParam, reportingDate],
  );

  const fetchInitialLoanAccountsSearchPage = useCallback(
    (queryParam: { loanAccountNumber?: string; customerName?: string }) => {
      table.resetSorting(true);
      setIsLoading(true);
      setQueryParams(queryParam);
      getLoanAccounts({
        variables: {
          reportingDate,
          isPreview,
          paginationOptions: { limit: PAGINATION_LIMIT, offset: 0 },
          queryParams: queryParam,
        },
      });
      scrollToTopOfElement('valley-bank-loanAccounts-table');
    },
    [getLoanAccounts, table, isPreview, reportingDate],
  );

  const fetchInitialLoanAccountsOrderByPage = useCallback(
    (newOrderByParam: OrderByConfig | null) => {
      setIsLoading(true);
      setOrderByParam(newOrderByParam);
      // includes any existing search params
      getLoanAccounts({
        variables: {
          reportingDate,
          isPreview,
          paginationOptions: { limit: PAGINATION_LIMIT, offset: 0 },
          queryParams,
          orderByParam: newOrderByParam,
        },
      });
    },
    [getLoanAccounts, queryParams, isPreview, reportingDate],
  );

  // only want to do this if we want to show a loading state when initial load (or metadata changes)
  useEffect(() => {
    setIsLoading(true);
    getLoanAccounts({
      variables: {
        reportingDate,
        isPreview,
        orderByParam,
        queryParams,
        paginationOptions: { limit: PAGINATION_LIMIT, offset: 0 },
      },
    });
  }, [getLoanAccounts, orderByParam, queryParams, isPreview, reportingDate]);

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <VStack w="100%" h="100%" justify="flex-start" spacing="24px" pt="24px">
      <HStack w="100%" spacing="16px" justifyContent="flex-end">
        <LoanAccountSearch
          searchLoanAccountNumber={searchLoanAccountNumber}
          searchCustomerName={searchCustomerName}
          setSearchLoanAccountNumber={setSearchLoanAccountNumber}
          setSearchCustomerName={setSearchCustomerName}
          fetchInitialLoanAccountsSearchPage={
            fetchInitialLoanAccountsSearchPage
          }
        />
        <ColumnDisplaySettingsButton
          initialColumns={columnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={LOAN_ACCOUNT_NAMES}
        />
        <CsvXlsxDownloadSelect
          dataRows={csvDownload.dataRows}
          headers={csvDownload.headers}
          filenamePrefix="loan-accounts"
        />
      </HStack>
      {/* {isLoading && (
        <ProgressAlert status="info">Loading Loan Accounts</ProgressAlert>
      )} */}
      {!isLoading && loanAccounts.length === 0 && (
        <>
          <Alert status="info">
            <Text>No Loan Accounts found</Text>
          </Alert>
        </>
      )}
      {loanAccounts.length > 0 && (
        <>
          <LoanAccountsTableContainer
            table={table}
            fetchInitialLoanAccountsOrderByPage={
              fetchInitialLoanAccountsOrderByPage
            }
            isLoading={isLoading}
            isError={Boolean(isError)}
          />
          <PaginationControl
            fetchNextPage={fetchLoanAccountsPage}
            paginationInfo={data?.allValleyBankLoanAccounts.paginationInfo}
          />
        </>
      )}
    </VStack>
  );
};
